import type { CrawlPage } from '../hooks/useCrawls'
import { decodeHtmlEntities } from '@/client/lib/decode'

/** Truncate string with ellipsis at character limit */
function truncate(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text
  return text.slice(0, maxChars).trimEnd() + '...'
}

/** Format URL as Google-style breadcrumb: domain > path > segments */
function urlBreadcrumb(url: string): string {
  try {
    const parsed = new URL(url)
    const parts = parsed.pathname.split('/').filter(Boolean)
    if (parts.length === 0) return parsed.hostname
    return `${parsed.hostname} › ${parts.join(' › ')}`
  } catch {
    return url
  }
}

export function SerpPreview({ page }: { page: CrawlPage }) {
  const title = decodeHtmlEntities(page.title || page.url)
  const description = decodeHtmlEntities(page.meta_description || '')
  const displayTitle = truncate(title, 60)
  const displayDesc = truncate(description, 160)
  const breadcrumb = urlBreadcrumb(page.url)

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground mb-2">Google Search Preview</p>
      <div className="rounded-lg border bg-white dark:bg-zinc-950 p-4 max-w-[600px]">
        {/* Favicon + breadcrumb */}
        <div className="flex items-center gap-2 mb-1">
          <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground">
            {page.url.charAt(8)?.toUpperCase() ?? 'W'}
          </div>
          <div className="min-w-0">
            <p className="text-xs text-[#4d5156] dark:text-zinc-400 truncate">{breadcrumb}</p>
          </div>
        </div>
        {/* Title */}
        <h3 className="text-xl leading-snug text-[#1a0dab] dark:text-blue-400 hover:underline cursor-default font-normal">
          {displayTitle}
        </h3>
        {/* Description */}
        {displayDesc ? (
          <p className="text-sm leading-relaxed text-[#4d5156] dark:text-zinc-400 mt-1">
            {displayDesc}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground/50 mt-1 italic">
            No meta description set — Google will generate a snippet from page content.
          </p>
        )}
      </div>

      {/* Warnings */}
      <div className="flex flex-wrap gap-2 text-xs">
        {!page.title && (
          <span className="text-destructive">Missing title tag</span>
        )}
        {page.title_length > 60 && (
          <span className="text-yellow-600 dark:text-yellow-400">
            Title may be truncated ({page.title_length} chars)
          </span>
        )}
        {!page.meta_description && (
          <span className="text-destructive">Missing meta description</span>
        )}
        {page.meta_description_length > 160 && (
          <span className="text-yellow-600 dark:text-yellow-400">
            Description may be truncated ({page.meta_description_length} chars)
          </span>
        )}
      </div>
    </div>
  )
}
